/**
 * Builds the doc-engine runtime the static halves render through - the same way the changes view
 * builds it (see `useDiffComputation.hook.ts`), because it is the same renderer.
 *
 * Everything comes from the WHOLE document, never the editor: the editor holds one part, so a
 * heading tree or caption map built from it would resolve only that part's references.
 */
import { useContext, useEffect, useSyncExternalStore } from 'react';

import { SignInContext } from '@modules/users';

import { useGetBlob } from '../../api';
import {
  createFrontendContentHooks,
  createFrontendRuntime,
  type TVersionHeadingsMap,
} from '../../components/changesRuntime';
import DocumentContext from '../../contexts/DocumentContext.context';
import flattenTreeHeadings from '../../helpers/flattenTreeHeadings.helper';
import { makeBlockHeaderRenderer } from './blockHeader';
import { elementList, subscribeElements } from '../editor/controller';
import { setStaticRenderContext } from './staticRenderCache';
import type { TCaptionMap } from '@shared/backendFrontend/captionHelpersFe';
import type { THeading } from '../../types';

/**
 * Everything the doc-engine draws that the stored html does not carry, as `id -> what it draws`.
 *
 * The cache compares this against the last one to know which elements to draw again. Only the values
 * that reach the screen belong here: adding one that changes on every keystroke would redraw the whole
 * static half each time.
 */
const drawnFromRuntime = (headings: THeading[], captions: TCaptionMap): Map<string, string> => {
  const drawn = new Map<string, string>();
  headings.forEach((heading) => drawn.set(heading.id, `${heading.numbering ?? ''}|${heading.text}`));
  Object.entries(captions).forEach(([id, caption]) =>
    drawn.set(id, `${caption.kind}|${caption.index}|${caption.content}`)
  );
  return drawn;
};

const useStaticRenderContext = () => {
  const context = useContext(DocumentContext);
  const { mock } = useContext(SignInContext).organization;
  const getBlob = useGetBlob();
  // A dependency, not just a guard: this hook lives in `Content`, whose effects run before its
  // parent's, so the first pass has no document yet and nothing else would bring it back.
  const elements = useSyncExternalStore(subscribeElements, elementList);
  // Wait for the caption map: `renderedElement` caches by element html and only invalidates per
  // document/version, so an element rendered without it keeps that rendering for the session.
  const captionsReady = context?.wholeCaptionsReady ?? false;

  const doc = context?.doc;
  const version = context?.version;
  const versionId = context?.versionId;
  const token = context?.permissions.token;
  const treeHeadings = context?.treeHeadings;
  // Already the whole document's captions merged with the editor's - see DocumentContext.
  const captionsMap = context?.captionsMap;
  const resolvedItemNames = context?.resolvedItemNames;

  useEffect(() => {
    if (!doc || !version || !versionId || !elements?.length || !captionsReady || !resolvedItemNames) return;

    // Live tree when the editor has produced one (it covers the whole document - see `headingTree`),
    // the persisted array otherwise. Same choice the changes view makes.
    const currentHeadings = treeHeadings?.length ? flattenTreeHeadings(treeHeadings) : version.headings;

    const versionHeadingsMap: TVersionHeadingsMap = {};
    /**
     * The draft's headings as the editor has them, not as they were last saved.
     *
     * A reference to a heading in the same document draws that heading's TEXT from this map. Reading
     * the saved copy meant renaming a heading left every reference to it in the static half showing
     * the old title until the document was saved and fetched again - while the same reference inside
     * the editor showed the new one.
     */
    if (doc.draft) versionHeadingsMap['draft'] = versionId === 'draft' ? currentHeadings : doc.draft.headings;
    for (const v of doc.versions) versionHeadingsMap[v.id] = v.headings;

    const captions = captionsMap ?? {};
    setStaticRenderContext({
      runtime: createFrontendRuntime(
        doc,
        {
          version,
          headings: currentHeadings,
          captions,
          versionHeadingsMap,
        },
        undefined,
        getBlob,
        // The static half sits next to the editor, so its blocks need the editor's header.
        makeBlockHeaderRenderer({ doc, version, resolvedItemNames, versionId })
      ),
      hooks: createFrontendContentHooks(),
      options: { isH5RefEnabled: mock.isH5RefEnabled },
      documentId: doc._id,
      token: token ?? '',
      key: `${doc._id}|${versionId}`,
      drawnFromRuntime: drawnFromRuntime(currentHeadings, captions),
    });
    // No teardown. React runs it before every re-run, and this effect re-runs whenever the heading tree
    // or the caption map settles - each time leaving `renderedElement` with no context, so every element it
    // was asked for fell back to its RAW stored html: unresolved references, no colgroups, no caption
    // prefixes, images with no src. Measured at 14,178 such calls on one load, and raw html lays out at a
    // different height from rendered html, so the page moved every time. The next run replaces it.
  }, [
    elements,
    captionsReady,
    doc,
    version,
    versionId,
    token,
    treeHeadings,
    captionsMap,
    resolvedItemNames,
    mock.isH5RefEnabled,
  ]);
};

export default useStaticRenderContext;
